import { GalaxyKPIDrivers } from '@services/api/deckManagement/graphql/schema/API';
import { MenuOption } from 'types/Common.types';
import { EnhancedDeckline } from 'types/DecklinesContext.types';
import { KpiDimension } from 'types/PageSidePanel.types';

/**
 * ---------------------------------------------------------------------------
 * headerTree layout types (EE/HCTR pages — Epic G)
 *
 * Re-exports of the generated getPageLayout node types plus the union aliases
 * the FE needs (the codegen emits the members but not a named union). These
 * describe the DEPLOYED `sections[]` model: a page has `PageLayoutSection[]`,
 * each with its own `columnHeaderTree` (GROUP|BLOCK roots) and flat `columns`.
 *
 * Consumed by useHeaderTreeColumns (GROUP/BLOCK -> ColGroupDef, LEAF -> ColDef).
 * ---------------------------------------------------------------------------
 */
export type {
  BlockNode,
  ColumnLeaf,
  GroupNode,
  PageLayout as HeaderTreePageLayout,
  LeafNode,
  PageLayoutSection,
} from '@services/api/deckManagement/graphql/schema/API';

// Local import for the union aliases below (the export above is type-only re-export).
import type {
  BlockNode as BlockNodeType,
  GroupNode as GroupNodeType,
  LeafNode as LeafNodeType,
} from '@services/api/deckManagement/graphql/schema/API';

/**
 * Any node in a section's header tree.
 *
 * Discriminate via `resolveNodeShape` in useHeaderTreeColumns rather than switching directly:
 * `__typename` is the primary discriminator and is reliable because
 * `scripts/addTypenameToUnions.js` injects it onto every union selection as a post-codegen step.
 * `kind` (HeaderNodeKind) is the nullable fallback, for a tree fetched with a thinner selection set
 * (raw Amplify codegen output omits union-level `__typename`). A node matching neither yields `null`.
 */
export type HeaderNode = GroupNodeType | BlockNodeType | LeafNodeType;

/**
 * A section's `columnHeaderTree` root: a grouping band (GROUP) or a BLOCK directly.
 * Mirrors the generated `HeaderRootNode = GroupNode | BlockNode` union.
 */
export type HeaderRootNode = GroupNodeType | BlockNodeType;

// ########### Existing Exports from Galaxy-based reporting follow below this line ##############

/**
 * Helper type for Page & Deckline Driver Updates.
 */
export type PartialKPIDrivers = Partial<Omit<GalaxyKPIDrivers, '__typename' | 'datasetSource'>>;

/**
 * Enum for context menu options
 */
export enum ContextMenuEnums {
  INSERT_ROW = 'Insert Row', // Single Insert row appears when no Page Decklines yet exist
  INSERT_ROW_ABOVE = 'Insert Row Above',
  INSERT_ROW_BELOW = 'Insert Row Below',
  FOOTNOTES = 'Footnotes',
  COMMENT = 'Comment',
  DECKLINE_HISTORY = 'Deckline History',
  DELETE_DECKLINE = 'Delete Deckline',
  DISPLAY_NAME_OVERRIDE = 'Edit Display Name',
  // -- DEV ADMIN OPTIONS (shown under the "Admin Dev Tools" submenu, DEV_TEAM_ADMIN only) --
  DEBUG_OUTPUT_DECKLINE = 'Deckline Console Debug', // Outputs deckline data directly to the browser console
  DEBUG_OUTPUT_CALC_KPI_IDS = 'Calc Per-Column IDs Debug', // Outputs a calc row's kpi_id_by_override to the console
}

/**
 * Right-click context menu options for a deckline, split by audience. Page.tsx builds these per
 * right-clicked row; SectionGrid renders them as AG Grid context menu items.
 */
export interface DecklineContextMenuOptions {
  /** Options shown directly on the menu. */
  options: MenuOption<ContextMenuEnums>[];
  /** Developer/admin options shown under the "Admin Dev Tools" submenu; empty hides the submenu. */
  devAdminOptions: MenuOption<ContextMenuEnums>[];
}

/**
 * Known granularity options for pages.
 */
export enum PageGranularity {
  MTD = 'MTD',
  QTD = 'QTD',
  YTD = 'YTD',
  Summary = 'Summary',
  Fx = 'Fx',
}

/**
 * Interface for the row data used in the Page Deckline view table.
 */
export interface DecklineRowData extends Record<KpiDimension, string | null> {
  id: number;
  rowNumber: string | number;
  kpi_name?: string | null;
  is_subtotal_highlighted?: boolean;
  rawDecklineData: EnhancedDeckline;
}

/**
 * Interface for column template and mapping group configuration
 * Used to define the template and mapping group for deck columns
 */
export interface ColumnTemplateMapping {
  column_template: string;
  column_mapping_group: string;
}
